import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  ComposedChart,
  Area,
  AreaChart,
  ScatterChart,
  Scatter
} from 'recharts';
import { Target, TrendingUp, Download, Eye, Bot, User, Zap, Award, AlertCircle, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { BatchRunData } from '../../../utils/dashboardMockData';

interface QualityTabProps {
  data: QualityMetric;
}
export interface QualityMetric {
  overviewData: {
    aiDecisionAccuracy: {
      accuracy: number
      totalCorrectAiDecisions: number;
      totalChartsReviewed: number;

    }
    auditCoverage: {
      coverage: number
      totalAiDecisions: number;
      totalChartsReviewed: number;

    }
    falsePositiveRate: {
      rate: number
      totalAiApproved: number;
      falsePositiveCount: number;
    }
    falseNegativeRate: {
      rate: number
      totalAiSendForCorrection: number;
      falseNegativeCount: number;
    }
  }
  trendData: any[]
}

export function QualityTab({ data }: QualityTabProps) {

  // AI Decision Validation Analytics (Auditor as Gold Standard)
  // const aiValidationAnalytics = React.useMemo(() => {
  //   if (!timeSeriesData || timeSeriesData.length === 0) return null;

  //   const totals = timeSeriesData.reduce((acc, item) => {
  //     // Simulate AI vs Auditor validation data
  //     const aiDecisions = item.aiProcessed || 0;
  //     const auditorReviews = Math.floor(aiDecisions * 0.3); // 30% of AI decisions get auditor review

  //     // Agreement rates (AI decisions that match auditor validation)
  //     const agreementRate = 0.82 + Math.random() * 0.15; // 82-97% agreement rate
  //     const aiCorrectDecisions = Math.floor(auditorReviews * agreementRate);
  //     const aiIncorrectDecisions = auditorReviews - aiCorrectDecisions;

  //     // False positives/negatives simulation
  //     const aiFalsePositives = Math.floor(aiIncorrectDecisions * 0.6); // AI approved but auditor rejected
  //     const aiFalseNegatives = aiIncorrectDecisions - aiFalsePositives; // AI rejected but auditor approved

  //     return {
  //       totalAIDecisions: acc.totalAIDecisions + aiDecisions,
  //       auditorReviews: acc.auditorReviews + auditorReviews,
  //       aiCorrectDecisions: acc.aiCorrectDecisions + aiCorrectDecisions,
  //       aiIncorrectDecisions: acc.aiIncorrectDecisions + aiIncorrectDecisions,
  //       aiFalsePositives: acc.aiFalsePositives + aiFalsePositives,
  //       aiFalseNegatives: acc.aiFalseNegatives + aiFalseNegatives
  //     };
  //   }, {
  //     totalAIDecisions: 0,
  //     auditorReviews: 0,
  //     aiCorrectDecisions: 0,
  //     aiIncorrectDecisions: 0,
  //     aiFalsePositives: 0,
  //     aiFalseNegatives: 0
  //   });

  //   const aiAccuracy = totals.auditorReviews > 0 ? (totals.aiCorrectDecisions / totals.auditorReviews) * 100 : 0;
  //   const falsePositiveRate = totals.auditorReviews > 0 ? (totals.aiFalsePositives / totals.auditorReviews) * 100 : 0;
  //   const falseNegativeRate = totals.auditorReviews > 0 ? (totals.aiFalseNegatives / totals.auditorReviews) * 100 : 0;
  //   const auditCoverage = totals.totalAIDecisions > 0 ? (totals.auditorReviews / totals.totalAIDecisions) * 100 : 0;

  //   return {
  //     totals,
  //     aiAccuracy,
  //     falsePositiveRate,
  //     falseNegativeRate,
  //     auditCoverage,
  //     qualityScore: aiAccuracy * (auditCoverage / 100), // Weighted by audit coverage
  //     confidenceLevel: aiAccuracy > 90 ? 'High' : aiAccuracy > 80 ? 'Medium' : 'Low'
  //   };
  // }, [timeSeriesData]);

  // // AI Decision Validation data for charts
  // const validationData = React.useMemo(() => {
  //   if (!aiValidationAnalytics) return [];

  //   return [
  //     {
  //       category: 'AI Correct',
  //       value: aiValidationAnalytics.totals.aiCorrectDecisions,
  //       percentage: aiValidationAnalytics.aiAccuracy,
  //       color: '#10B981'
  //     },
  //     {
  //       category: 'AI Incorrect', 
  //       value: aiValidationAnalytics.totals.aiIncorrectDecisions,
  //       percentage: 100 - aiValidationAnalytics.aiAccuracy,
  //       color: '#F59E0B'
  //     }
  //   ];
  // }, [aiValidationAnalytics]);

  // // Error analysis data
  // const errorAnalysisData = React.useMemo(() => {
  //   if (!aiValidationAnalytics) return [];

  //   return [
  //     {
  //       type: 'False Positives',
  //       count: aiValidationAnalytics.totals.aiFalsePositives,
  //       rate: aiValidationAnalytics.falsePositiveRate,
  //       description: 'AI approved but auditor rejected',
  //       color: '#EF4444'
  //     },
  //     {
  //       type: 'False Negatives', 
  //       count: aiValidationAnalytics.totals.aiFalseNegatives,
  //       rate: aiValidationAnalytics.falseNegativeRate,
  //       description: 'AI rejected but auditor approved',
  //       color: '#F97316'
  //     }
  //   ];
  // }, [aiValidationAnalytics]);

  // Validation metrics matrix 
  const validationMatrix = React.useMemo(() => {
    // if (!aiValidationAnalytics) return [];

    return [
      {
        metric: 'AI Decision Accuracy',
        value: data?.overviewData?.aiDecisionAccuracy?.accuracy,
        // target: 90,
        totalDecision: data?.overviewData?.aiDecisionAccuracy?.totalCorrectAiDecisions,
        totalCharts: data?.overviewData?.aiDecisionAccuracy?.totalChartsReviewed,
        status: data?.overviewData?.aiDecisionAccuracy?.accuracy >= 90 ? 'excellent' : data?.overviewData?.aiDecisionAccuracy?.accuracy >= 80 ? 'good' : 'needs-improvement',
        description: 'Percentage of AI decisions that match auditor validation'
      },
      {
        metric: 'Audit Coverage',
        value: data?.overviewData?.auditCoverage?.coverage,
        // target: 25,
        totalCharts: data?.overviewData?.auditCoverage?.totalChartsReviewed,
        totalDecisions: data?.overviewData?.auditCoverage?.totalAiDecisions,
        status: data?.overviewData?.auditCoverage?.coverage >= 25 ? 'excellent' : data?.overviewData?.auditCoverage?.coverage >= 15 ? 'good' : 'needs-improvement',
        description: 'Percentage of AI decisions reviewed by auditors'
      },
      {
        metric: 'False Positive Rate',
        value: data?.overviewData?.falsePositiveRate?.rate,
        // target: 5,
        falseCount: data?.overviewData?.falsePositiveRate?.falsePositiveCount,
        totalCharts: data?.overviewData?.falsePositiveRate?.totalAiApproved,
        status: data?.overviewData?.falsePositiveRate?.rate <= 5 ? 'excellent' : data?.overviewData?.falsePositiveRate?.rate <= 10 ? 'good' : 'needs-improvement',
        description: 'AI approved decisions rejected by auditors'
      },
      {
        metric: 'False Negative Rate',
        value: data?.overviewData?.falseNegativeRate?.rate,
        // target: 5,
        falseCount: data?.overviewData?.falseNegativeRate?.falseNegativeCount,
        totalCharts: data?.overviewData?.falseNegativeRate?.totalAiSendForCorrection,
        status: data?.overviewData?.falseNegativeRate?.rate <= 5 ? 'excellent' : data?.overviewData?.falseNegativeRate?.rate <= 10 ? 'good' : 'needs-improvement',
        description: 'AI rejected decisions approved by auditors'
      }
    ];
  }, [data?.overviewData]);

  // Time series validation data
  // const timeValidationData = React.useMemo(() => {
  //   return timeSeriesData.map(item => {
  //     const aiDecisions = item.aiProcessed || 0;
  //     const auditorReviews = Math.floor(aiDecisions * (0.25 + Math.random() * 0.1)); // 25-35% review rate
  //     const accuracy = 82 + Math.random() * 15; // 82-97% accuracy
  //     const fpRate = Math.random() * 8; // 0-8% false positive rate
  //     const fnRate = Math.random() * 6; // 0-6% false negative rate

  //     return {
  //       date: item.date,
  //       aiDecisions: aiDecisions,
  //       auditorReviews: auditorReviews,
  //       accuracy: accuracy,
  //       falsePositiveRate: fpRate,
  //       falseNegativeRate: fnRate,
  //       qualityScore: accuracy * (auditorReviews / (aiDecisions || 1)),
  //       auditCoverage: aiDecisions > 0 ? (auditorReviews / aiDecisions) * 100 : 0
  //     };
  //   });
  // }, [timeSeriesData]);

  // Quality funnel data (simplified)
  // const funnelData = React.useMemo(() => {
  //   const totalProcessed = data?.reduce((sum, batch) => sum + batch.totalProcessed, 0);
  //   const approved = data?.reduce((sum, batch) => sum + batch.approved, 0);
  //   const sentForCorrection = data?.reduce((sum, batch) => sum + batch.sentForCorrection, 0);

  //   return [
  //     { name: 'Approved', value: approved, color: '#10B981', percentage: totalProcessed > 0 ? (approved / totalProcessed) * 100 : 0 },
  //     { name: 'Sent for Correction', value: sentForCorrection, color: '#F59E0B', percentage: totalProcessed > 0 ? (sentForCorrection / totalProcessed) * 100 : 0 }
  //   ];
  // }, [data]);

  // Chart type quality breakdown for drilldown table
  // const qualityBreakdown = React.useMemo(() => {
  //   const breakdown = new Map();

  //   data.forEach(batch => {
  //     const key = batch.chartType || 'Unknown';
  //     const date = format(batch.date, 'yyyy-MM-dd');

  //     if (!breakdown.has(key)) {
  //       breakdown.set(key, {
  //         chartType: batch.chartType || 'Unknown',
  //         batches: new Map()
  //       });
  //     }

  //     const chartTypeData = breakdown.get(key);
  //     if (!chartTypeData.batches.has(date)) {
  //       chartTypeData.batches.set(date, {
  //         date: batch.date,
  //         totalProcessed: 0,
  //         approved: 0,
  //         sentForCorrection: 0,
  //         batchIds: []
  //       });
  //     }

  //     const dayData = chartTypeData.batches.get(date);
  //     dayData.totalProcessed += batch.totalProcessed;
  //     dayData.approved += batch.approved;
  //     dayData.sentForCorrection += batch.sentForCorrection;
  //     dayData.batchIds.push(batch.batchId);
  //   });

  //   const result: any[] = [];
  //   breakdown.forEach((chartTypeData) => {
  //     chartTypeData.batches.forEach((dayData: any) => {
  //       result.push({
  //         date: dayData.date,
  //         chartType: chartTypeData.chartType,
  //         totalProcessed: dayData.totalProcessed,
  //         approved: dayData.approved,
  //         sentForCorrection: dayData.sentForCorrection,
  //         approvalRate: dayData.totalProcessed > 0 ? (dayData.approved / dayData.totalProcessed) * 100 : 0,
  //         batchIds: dayData.batchIds
  //       });
  //     });
  //   });

  //   return result.sort((a, b) => b.date.getTime() - a.date.getTime());
  // }, [data]);

  // const handleExportQualityData = () => {
  //   // Export AI vs Auditor performance data
  //   const csvContent = [
  //     ['Date', 'Chart Type', 'Total Processed', 'Approved', 'Sent for Correction', 'Approval Rate %'],
  //     ...qualityBreakdown.map(row => [
  //       format(row.date, 'yyyy-MM-dd'),
  //       row.chartType,
  //       row.totalProcessed,
  //       row.approved,
  //       row.sentForCorrection,
  //       row.approvalRate.toFixed(2)
  //     ])
  //   ].map(row => row.join(',')).join('\n');

  //   const blob = new Blob([csvContent], { type: 'text/csv' });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement('a');
  //   a.href = url;
  //   a.download = `chart_type_performance_${format(new Date(), 'yyyy-MM-dd')}.csv`;
  //   a.click();
  //   URL.revokeObjectURL(url);
  // };

  // const handleExportValidationAnalytics = () => {
  //   // if (!aiValidationAnalytics) return;

  //   const csvContent = [
  //     ['Metric', 'Value', 'Description'],
  //     ['AI Decision Accuracy %', aiValidationAnalytics.aiAccuracy.toFixed(2), 'Percentage of AI decisions that match auditor validation'],
  //     ['Audit Coverage %', aiValidationAnalytics.auditCoverage.toFixed(2), 'Percentage of AI decisions reviewed by auditors'], 
  //     ['False Positive Rate %', aiValidationAnalytics.falsePositiveRate.toFixed(2), 'AI approved but auditor rejected'],
  //     ['False Negative Rate %', aiValidationAnalytics.falseNegativeRate.toFixed(2), 'AI rejected but auditor approved'],
  //     ['Quality Score', aiValidationAnalytics.qualityScore.toFixed(2), 'Weighted accuracy by audit coverage'],
  //     ['Total AI Decisions', aiValidationAnalytics.totals.totalAIDecisions, 'Total decisions made by AI platform'],
  //     ['Auditor Reviews', aiValidationAnalytics.totals.auditorReviews, 'Total auditor validations performed']
  //   ].map(row => row.join(',')).join('\n');

  //   const blob = new Blob([csvContent], { type: 'text/csv' });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement('a');
  //   a.href = url;
  //   a.download = `ai_validation_analytics_${format(new Date(), 'yyyy-MM-dd')}.csv`;
  //   a.click();
  //   URL.revokeObjectURL(url);
  // };

  const getQualityBadgeColor = (rate: number) => {
    if (rate >= 90) return 'default'; // Green
    if (rate >= 80) return 'secondary'; // Yellow
    return 'destructive'; // Red
  };
  // const arr = Object.entries(data?.overviewData).map(([key, value]) => ({
  //   key,     // keep the key
  //   ...value // spread the inner object
  // }));
  // console.log(aiValidationAnalytics,validationMatrix,"qwqw")

  // const cleanData = data?.trendData?.map(d => ({
  //   date: d.date,
  //   aiDecisionsVolume: Number(d.aiDecisionsVolume),
  //   auditorReviews: Number(d.auditorReviewsVolume),
  //   aiAccuracy: parseFloat(d.aiAccuracy.replace('%', '')),
  //   auditCoverage: parseFloat(d.auditCoverage.replace('%', '')),
  //   falsePositiveRate: parseFloat(d.falsePositiveRate.replace('%', '')),
  //   falseNegativeRate: parseFloat(d.falseNegativeRate.replace('%', '')),
  // }));
  return (
    <div className="space-y-6">
      {/* AI Decision Validation Charts */}


      {/* Validation Metrics Matrix */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            AI Platform Quality Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {validationMatrix.map((metric, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">{metric.metric}</span>
                  <Badge variant={
                    metric.status === 'excellent' ? 'default' :
                      metric.status === 'good' ? 'secondary' : 'destructive'
                  }>
                    {metric.status === 'excellent' ? 'Excellent' :
                      metric.status === 'good' ? 'Good' : 'Needs Improvement'}
                  </Badge>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <div className="text-2xl font-bold text-blue-900">
                    {(() => {
                      if (!data?.overviewData) return `${metric.value || 0}%`;

                      const count = (() => {
                        switch (metric.metric) {
                          case 'AI Decision Accuracy':
                            return data?.overviewData?.aiDecisionAccuracy?.totalCorrectAiDecisions;
                          case 'Audit Coverage':
                            return data?.overviewData?.auditCoverage?.totalChartsReviewed;
                          case 'False Positive Rate':
                            return data?.overviewData?.falsePositiveRate?.falsePositiveCount;
                          case 'False Negative Rate':
                            return data?.overviewData?.falseNegativeRate?.falseNegativeCount;
                          default:
                            return 0;
                        }
                      })();

                      return (
                        <div className="flex items-baseline gap-2">
                          <div className="text-2xl font-bold text-blue-900">
                            {count?.toLocaleString() || 0}
                          </div>
                          <div className="text-lg font-bold text-gray-600">
                            ({metric.value || 0}%)
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {/* Target: {metric.target}%
                     */}{(() => {
                      switch (metric.metric) {
                        case 'AI Decision Accuracy':
                          return `Total Charts Reviewed: ${metric?.totalCharts || 0}`;
                        case 'Audit Coverage':
                          return `Total AI Decisions: ${metric?.totalDecisions || 0}`;
                        case 'False Positive Rate':
                          return `AI Approved Count: ${metric?.totalCharts || 0}`;
                        case 'False Negative Rate':
                          return `AI Send For Correction: ${metric?.totalCharts || 0}`;
                        default:
                          return metric.description;
                      }
                    })()}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {(() => {
                    switch (metric.metric) {
                      case 'AI Decision Accuracy':
                        return 'Correct AI decisions validated by auditors';
                      case 'Audit Coverage':
                        return 'AI decisions reviewed by auditors';
                      case 'False Positive Rate':
                        return 'AI approved but auditor rejected';
                      case 'False Negative Rate':
                        return 'AI rejected but auditor approved';
                      default:
                        return metric.description;
                    }
                  })()}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* AI Validation Trends Over Time */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            AI Validation Trends Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">

            {data?.trendData && data?.trendData?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data?.trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" stroke="#6B7280" fontSize={11} />
                  <YAxis yAxisId="left" stroke="#6B7280" fontSize={11} domain={[0, 100]} />
                  <YAxis yAxisId="right" orientation="right" stroke="#6B7280" fontSize={11} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    formatter={(value: number, name: string) => [
                      name.includes('Rate') || name.includes('accuracy') || name.includes('Coverage') ? `${value.toFixed(1)}` : value.toLocaleString(),
                      name
                    ]}
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="aiDecisionsVolume"
                    fill="#3B82F640"
                    stroke="#3B82F6"
                    name="AI Decisions Volume"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="auditorReviewsVolume"
                    fill="#10B98160"
                    stroke="#10B981"
                    name="Auditor Reviews Volume"
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="aiAccuracy"
                    stroke="#059669"
                    strokeWidth={3}
                    name="AI Accuracy %"
                    dot={{ fill: '#059669', strokeWidth: 2, r: 4 }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="auditCoverage"
                    stroke="#1D4ED8"
                    strokeWidth={2}
                    name="Audit Coverage %"
                    dot={{ fill: '#1D4ED8', strokeWidth: 2, r: 3 }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="falsePositiveRate"
                    stroke="#DC2626"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="False Positive Rate %"
                    dot={{ fill: '#DC2626', strokeWidth: 2, r: 3 }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="falseNegativeRate"
                    stroke="#F59E0B"
                    strokeWidth={2}
                    strokeDasharray="3 3"
                    name="False Negative Rate %"
                    dot={{ fill: '#F59E0B', strokeWidth: 2, r: 3 }}
                  />
                  <Legend />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No data available for the selected period
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Platform Quality Insights */}


      {/* Quality Drilldown Table - Simplified */}
      {/* <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Chart Type Performance Breakdown</CardTitle>
          <Button onClick={handleExportQualityData} size="sm" variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Data
          </Button>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Chart Type</TableHead>
                  <TableHead className="text-right">Total Processed</TableHead>
                  <TableHead className="text-right">Approved</TableHead>
                  <TableHead className="text-right">Sent for Correction</TableHead>
                  <TableHead className="text-right">Approval Rate</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {qualityBreakdown.slice(0, 20).map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{format(row.date, 'MMM dd, yyyy')}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{row.chartType}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{row.totalProcessed.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-green-600">{row.approved.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-orange-600">{row.sentForCorrection.toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={getQualityBadgeColor(row.approvalRate)}>
                        {row.approvalRate.toFixed(1)}%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {qualityBreakdown.length > 20 && (
            <div className="mt-4 text-center">
              <Button variant="outline" size="sm">
                Load More ({qualityBreakdown.length - 20} remaining)
              </Button>
            </div>
          )}
        </CardContent>
      </Card> */}
    </div>
  );
}